import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { fetchStatus, setShowErrorModal } from "./preferenceSlice";
import { getUrl } from "../../helpers/getUrl";

const initialState = {
  customerNumberPreference: {
    value: "tx",
  },
  txFilterPreference: undefined,
  hubspotFilterPreference: undefined,
  hubspotCustomerNumberPreference: undefined,
  currencyPreference: undefined,
  languagePreference: undefined,
  accountManagerPreference: undefined,
  departmentPreference: undefined,
  isContactSyncRulesSaved: false,
  isContactDefaultMappingsSaved: false,
  customerNumberFields: [
    {
      label: "Let Tripletex create the customer number",
      value: "tx",
    },
    {
      label: "Take Tripletex customer number from HubSpot",
      value: "hubspot",
    },
  ],
  txCustomerNumberPreference: {
    name: "Tripletex Customer Number",
    value: "tripletexCustomerNumber",
  },
  txCustomerNumberDropdownField: [
    {
      HRF: "Tripletex Customer Number",
      CRF: "tripletexCustomerNumber",
    },
  ],
  txFieldsData: {
    loading: false,
    fieldsData: {},
    error: "",
    currentRequestId: undefined,
  },
};

export const fetchDefaultTxFields = createAsyncThunk(
  "contact/fetchDefaultTxFields",
  async (portalId, { dispatch }) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl("REACT_APP_GET_DEFAULT_TX_FIELDS")}?portalId=${portalId}`
      );
      return response;
    } catch (error) {
      let errorMessage;
      if (error?.response && error.response?.data?.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(
        setShowErrorModal({
          message: errorMessage,
        })
      );
      throw error;
    }
  }
);

const contactSlice = createSlice({
  name: "contactSlice",
  initialState,
  reducers: {
    onSetCustomerNumberPreference(state, { payload }) {
      state.customerNumberPreference = {
        value: payload.selectedOption,
      };

      if (state.customerNumberPreference.value === "tx") {
        state.hubspotCustomerNumberPreference = {
          name: "Do not add it anywhere",
          value: "doNotAdd",
        };
      } else {
        state.hubspotCustomerNumberPreference = undefined;
      }
      state.isContactSyncRulesSaved = false;
    },

    onSetSelectContactFields(state, { payload }) {
      const { selectingFor, selectedOption } = payload;

      if (selectingFor === "hubspotFilterFields") {
        state.hubspotFilterPreference = {
          name: selectedOption.HRF,
          value: selectedOption.CRF,
        };
        state.isContactSyncRulesSaved = false;
      } else if (selectingFor === "txFilterFields") {
        state.txFilterPreference = {
          name: selectedOption.HRF,
          value: selectedOption.CRF,
        };
        state.isContactSyncRulesSaved = false;
      } else if (selectingFor === "hsCustomerNumberField") {
        state.hubspotCustomerNumberPreference = {
          name: selectedOption.HRF,
          value: selectedOption.CRF,
        };
        state.isContactSyncRulesSaved = false;
      } else if (selectingFor === "currency") {
        state.currencyPreference = {
          name: selectedOption.title,
          value: selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (selectingFor === "language") {
        state.languagePreference = {
          name: selectedOption.title,
          value: selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (selectingFor === "accountManager") {
        state.accountManagerPreference = {
          name: selectedOption.title,
          value: selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (selectingFor === "department") {
        state.departmentPreference = {
          name: selectedOption.title,
          value: selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      }
    },

    setIsContactDataSaved(state, { payload }) {
      if (payload.tab === "syncRules") {
        state.isContactSyncRulesSaved = true;
      } else if (payload.tab === "defaultMappings") {
        state.isContactDefaultMappingsSaved = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStatus.fulfilled, (state, action) => {
      const { preference } = action.payload;

      if (preference !== undefined && Object.keys(preference).length > 0) {
        const { customer } = preference;

        if (customer && Object.keys(customer).length > 0) {
          if (customer?.hubspotCustomerNumber) {
            state.customerNumberPreference.value = !customer
              .hubspotCustomerNumber.direction
              ? "tx"
              : customer.hubspotCustomerNumber.direction;

            if (customer?.hubspotCustomerNumber?.mapping) {
              state.hubspotCustomerNumberPreference = customer
                ?.hubspotCustomerNumber?.mapping?.hubspotProperty?.value
                ? customer.hubspotCustomerNumber.mapping.hubspotProperty
                : undefined;
            }
          }

          if (customer?.filterOptions) {
            state.hubspotFilterPreference = customer?.filterOptions
              ?.filterHsProperty?.value
              ? customer.filterOptions.filterHsProperty
              : undefined;
            state.txFilterPreference = customer?.filterOptions?.filterTxProperty
              ?.value
              ? customer.filterOptions.filterTxProperty
              : undefined;
          }

          // if (customer?.txFields) {
          //   const { language, currency, accountManager, department } =
          //     customer.txFields;

          //   state.languagePreference = language?.value ? language : undefined;
          //   state.currencyPreference = currency?.value ? currency : undefined;
          //   state.accountManagerPreference = accountManager?.value
          //     ? accountManager
          //     : undefined;
          //   state.departmentPreference = department?.value
          //     ? department
          //     : undefined;
          // }
        }
      }

      // if (
      //   state.languagePreference &&
      //   state.currencyPreference &&
      //   state.accountManagerPreference &&
      //   state.departmentPreference
      // ) {
      //   state.isContactDefaultMappingsSaved = true;
      // }

      if (
        state.txFilterPreference &&
        state.hubspotFilterPreference &&
        state.hubspotCustomerNumberPreference
      ) {
        state.isContactSyncRulesSaved = true;
      }
    });
    builder.addCase(fetchDefaultTxFields.pending, (state, action) => {
      if (!state.txFieldsData.loading) {
        state.txFieldsData.loading = true;
        state.txFieldsData.currentRequestId = action.meta.requestId;
      }
    });
    builder.addCase(fetchDefaultTxFields.fulfilled, (state, action) => {
      if (
        state.txFieldsData.loading &&
        state.txFieldsData.currentRequestId === action.meta.requestId
      ) {
        state.txFieldsData.loading = false;
        state.txFieldsData.fieldsData = action.payload;
        state.txFieldsData.error = "";
        state.txFieldsData.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchDefaultTxFields.rejected, (state, action) => {
      if (
        state.txFieldsData.loading &&
        state.txFieldsData.currentRequestId === action.meta.requestId
      ) {
        state.txFieldsData.loading = false;
        state.txFieldsData.fieldsData = {};
        state.txFieldsData.error = action.error.message ?? "";
        state.txFieldsData.currentRequestId = undefined;
      }
    });
  },
});

export const {
  onSetSelectContactFields,
  onSetCustomerNumberPreference,
  setIsContactDataSaved,
} = contactSlice.actions;

export default contactSlice.reducer;
